/**
 * MagicMomentStep Component
 *
 * Step 4: AI generates the assessment package.
 * Shows theatrical animated progress while AI creates competencies, scenario, persona, and rubric.
 * This is the "magic moment" from VISION.md - the trainer watches AI do the heavy lifting.
 *
 * Uses SSE via useGenerationStream hook for real-time progress (with polling fallback).
 * Renders MagicMomentTheatrical for a rich, multi-agent experience.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../lib/api';
import { MagicMomentTheatrical } from '../MagicMomentTheatrical';
import { useGenerationStream } from '../../hooks/useGenerationStream';
import type { PackageSummary } from '../../lib/types';

interface MagicMomentStepProps {
  /** Assessment ID to generate package for */
  assessmentId: string;
  /** Called when generation completes successfully */
  onComplete: (packageSummary: PackageSummary) => void;
  /** Called if generation fails */
  onError: (error: string) => void;
  /** Called if user wants to retry after failure */
  onRetry?: () => void;
}

export function MagicMomentStep({
  assessmentId,
  onComplete,
  onError,
  onRetry,
}: MagicMomentStepProps) {
  const [isTriggering, setIsTriggering] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  // SSE-based progress stream (enabled after trigger succeeds)
  const stream = useGenerationStream({
    assessmentId,
    enabled: hasTriggered && !triggerError,
  });

  // Handle completion
  useEffect(() => {
    if (stream.isComplete) {
      // Try to get the package summary
      if (stream.packageSummary) {
        onComplete(stream.packageSummary);
      } else {
        // SSE completed but no package summary from polling
        // Fetch it directly
        apiClient.getGenerationStatus(assessmentId).then((result) => {
          if (result.response.data?.package) {
            onComplete(result.response.data.package);
          }
        }).catch(() => {
          // Best effort - the parent can handle this
        });
      }
    }
  }, [stream.isComplete, stream.packageSummary, assessmentId, onComplete]);

  // Handle failure
  useEffect(() => {
    if (stream.isFailed && stream.errorMessage) {
      onError(stream.errorMessage);
    }
  }, [stream.isFailed, stream.errorMessage, onError]);

  // Trigger generation
  const triggerGeneration = useCallback(async () => {
    setIsTriggering(true);
    setTriggerError(null);

    try {
      const result = await apiClient.triggerGeneration(assessmentId);

      if (result.response.error) {
        // If there's already an active job, just start streaming
        if (result.response.status === 409) {
          setHasTriggered(true);
          setIsTriggering(false);
          return;
        }
        const errorMsg = result.response.error.message;
        setTriggerError(errorMsg);
        onError(errorMsg);
        setIsTriggering(false);
        return;
      }

      setHasTriggered(true);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to start generation';
      setTriggerError(errorMsg);
      onError(errorMsg);
    } finally {
      setIsTriggering(false);
    }
  }, [assessmentId, onError]);

  // Auto-trigger on mount (only once, not on error)
  useEffect(() => {
    if (!hasTriggered && !isTriggering && !triggerError) {
      triggerGeneration();
    }
  }, [hasTriggered, isTriggering, triggerError, triggerGeneration]);

  // Retry handler
  const handleRetry = () => {
    setHasTriggered(false);
    setTriggerError(null);
    if (onRetry) {
      onRetry();
    }
  };

  return (
    <div>
      {/* Trigger error */}
      {triggerError && (
        <div
          style={{
            padding: 24,
            borderRadius: 16,
            background: 'var(--red-50)',
            border: '1px solid var(--red-100)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 32, marginBottom: 12 }}>{'\u26A0\uFE0F'}</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--red-700)', marginBottom: 4 }}>
            Failed to start generation
          </div>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--red-600)' }}>
            {triggerError}
          </p>
          <button
            type="button"
            onClick={handleRetry}
            style={{
              padding: '10px 24px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--sky-500)',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Theatrical generation progress */}
      {!triggerError && hasTriggered && (
        <MagicMomentTheatrical
          events={stream.events}
          currentPhase={stream.currentPhase}
          agents={stream.agents}
          progress={stream.progress}
          isComplete={stream.isComplete}
          isFailed={stream.isFailed}
          errorMessage={stream.errorMessage}
          isStreaming={stream.isStreaming}
        />
      )}

      {/* Initial loading state before trigger completes */}
      {!triggerError && !hasTriggered && isTriggering && (
        <div
          style={{
            padding: 40,
            textAlign: 'center',
            borderRadius: 20,
            background: 'linear-gradient(135deg, var(--sky-50) 0%, #ffffff 50%, var(--slate-50) 100%)',
            border: '1px solid var(--sky-100)',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              margin: '0 auto 16px',
              borderRadius: '50%',
              border: '3px solid var(--sky-100)',
              borderTopColor: 'var(--sky-500)',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <div style={{ fontSize: 15, color: 'var(--slate-500)' }}>
            Starting generation...
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
    </div>
  );
}
